import type { FirmwareSection, LicenseService } from "./overview.types";

export const EMPTY = "—";
export const NO_READING = "No reading";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since an ISO instant; `null` for an unreadable one. */
export function daysSince(iso: string, now = Date.now()): number | null {
    const at = Date.parse(iso);
    if (Number.isNaN(at)) return null;
    return Math.max(0, Math.floor((now - at) / DAY_MS));
}

/** "just now", "12 min ago", "3 h ago", "2 d ago". */
export function timeAgo(iso: string, now = Date.now()): string {
    const at = Date.parse(iso);
    if (Number.isNaN(at)) return EMPTY;
    const minutes = Math.max(0, Math.round((now - at) / 60_000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
    return `${Math.floor(minutes / (24 * 60))} d ago`;
}

/** "Sep 7" — the year only when it is not this one. */
export function shortDate(iso: string, now = new Date()): string {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return EMPTY;
    return at.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    });
}

/** "07:12", on the viewer's clock. */
export function shortTime(iso: string): string {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return EMPTY;
    return at.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}

export function formatMemoryMb(mb: number | null): string {
    if (mb == null) return EMPTY;
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

const versionParts = (version: string) => version.split(".").map((part) => Number(part) || 0);

/** -1, 0, 1 — numeric per part, so 7.4.11 sorts after 7.4.9. */
export function compareVersions(a: string, b: string): number {
    const left = versionParts(a);
    const right = versionParts(b);
    for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0) return Math.sign(diff);
    }
    return 0;
}

/**
 * The version running and, when there is one, the mature release to move to. A
 * feature release (`newest_available` without maturity) is not offered: on a
 * customer's production firewall, "8.0.1 available" reads as advice we would not give.
 */
export function firmwareSummary(firmware: FirmwareSection): { value: string; update: string | null } {
    const current = firmware.current ?? EMPTY;
    const mature = firmware.newest_mature;
    if (!firmware.current || !mature || compareVersions(mature, firmware.current) <= 0) {
        return { value: current, update: null };
    }
    return { value: current, update: mature };
}

export const LICENSE_SERVICE_LABEL: Record<string, string> = {
    appctrl: "Application control",
    ips: "IPS",
    antivirus: "Antivirus",
    web_filtering: "Web filtering",
};

export const serviceLabel = (service: LicenseService) => LICENSE_SERVICE_LABEL[service.id] ?? service.id;
